/**
 * Rive layer for the HighLow benchmark port.
 *
 * Loads two artboards onto two stacked canvases — mirroring the two
 * `RiveWidgetController`s in
 *   packages/amghighlow_v2/lib/presentation/rive/rive_controller_manager.dart:
 *
 *     main    → highlow.riv          artboard "Main"   (cards, girl, shuffle, pointer)
 *     betting → highlowbetting.riv   artboard "Main"   (chip bar, High/Low click areas)
 *
 * Why one Rive instance per canvas: `@rive-app/webgl2` clears the canvas at
 * the start of every frame, so two instances sharing a canvas overwrite each
 * other.
 *
 * Card-slot inputs (`CardFace1`..`CardFace6`) are nested under path
 * `CardType`, set via `setNumberStateAtPath`.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import { MainInputs } from './riveInputs';

// ----------------------------------------------------------------------------
// Cached-input helpers (one per discovered SM input)
// ----------------------------------------------------------------------------

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

interface RiveTarget {
  key: TargetKey;
  file: string;
  stateMachine: string;
  instance: Rive;
  inputs: Map<string, AnyInput>;
}

export type TargetKey = 'main' | 'betting';

const FILES = {
  main: 'highlow.riv',
  betting: 'highlowbetting.riv',
} as const;

const ARTBOARDS = {
  main: 'Main',
  betting: 'Main',
} as const;

const STATE_MACHINES = {
  main: 'Main',
  betting: 'Main',
} as const;

// Per-canvas Rive Fit — MUST mirror the `fit:` on each RiveWidget in
// packages/amghighlow_v2/.../game_screen.dart. Both HighLow layers use cover:
//   main    (Positioned.fill)          → fit: Fit.cover   (game_screen.dart:365)
//   betting (Transform.scale(0.3) box) → fit: Fit.cover   (game_screen.dart:380)
// A single Fit.Contain for all canvases was wrong: the betting canvas is
// full-band (see index.html #rive-canvas-betting) and Contain shrank/letterboxed
// the artwork instead of filling the band like Flutter's cover.
const FITS: Record<TargetKey, Fit> = {
  main: Fit.Cover,
  betting: Fit.Cover,
};

// ----------------------------------------------------------------------------
// Public types
// ----------------------------------------------------------------------------

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'UIButtonClick'
  | 'sideAnimWin'
  | 'evtResultStop'
  | 'evtSoundOnOff'
  | 'evtMusicOnOff';

type RiveEventCallback = (data: Record<string, unknown>) => void;

// ----------------------------------------------------------------------------
// Card slot 1..6
// ----------------------------------------------------------------------------

type CardSlot = 1 | 2 | 3 | 4 | 5 | 6;

// The nested slot PATH for each of the 6 card positions. The face-selector
// number input `CardType` is set AT one of these paths (see setCardValue).
const CARD_SLOT_PATH: Record<CardSlot, string> = {
  1: MainInputs.cardSlot1,
  2: MainInputs.cardSlot2,
  3: MainInputs.cardSlot3,
  4: MainInputs.cardSlot4,
  5: MainInputs.cardSlot5,
  6: MainInputs.cardSlot6,
};

// ----------------------------------------------------------------------------
// Implementation
// ----------------------------------------------------------------------------

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([this.loadOne('main'), this.loadOne('betting')]);

    this.dumpInputs();
    this.emit('ready', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sm = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sm,
        autoplay: true,
        layout: new Layout({ fit: FITS[key], alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sm);
            const target: RiveTarget = {
              key,
              file,
              stateMachine: sm,
              instance: rive,
              inputs,
            };
            this.targets.set(key, target);
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(
            new Error(
              `Failed to load ${src} [${artboard}/${sm}]: ${String(err)}`,
            ),
          );
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.values()).map((i) => ({
        target: t.key,
        file: t.file,
        sm: t.stateMachine,
        name: i.name,
        type: i.type,
      }));
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.stateMachine}) — ${rows.length} input(s)`,
      );
      console.table(rows);
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as {
          data?: { name?: string; properties?: Record<string, unknown> };
        };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public high-level API — mirrors RiveControllerManager
  // -----------------------------------------------------------------

  /**
   * Reveal a card by selecting its face at one of the 6 nested slots.
   *
   * highlow.riv uses ONE reusable `CardFace` artboard whose number input
   * `CardType` picks the face, instanced at slot PATHS `CardFace1`..`CardFace6`.
   * The correct call is therefore input=`CardType`, path=slot:
   *     setNumberStateAtPath('CardType', value, 'CardFace1')
   *
   * The Flutter manager passes (inputName:'CardFaceN', path:'CardType') but only
   * works because of its INVERSE fallback `number('CardType', path:'CardFaceN')`
   * (rive_controller_manager.dart:193-198). WebGL2's setNumberStateAtPath does
   * NOT throw on a miss — it only console.warns and returns void — so a
   * try-both fallback would lock onto the first (wrong) form. We must pass the
   * one correct order here, or the input silently no-ops and every card stays on
   * its default face. (Same trap fixed in baccarat: CardType@PlayerCardN.)
   */
  setCardValue(slot: CardSlot, value: number): void {
    this.setNumberAtPath('main', MainInputs.cardTypeInput, value, CARD_SLOT_PATH[slot]);
  }

  /**
   * Round counter / current card pointer.
   *  0 = reset/shuffle visual
   *  1..6 = active slot
   */
  setRoundCounter(value: number): void {
    this.setNumberAny('main', MainInputs.numCardPlay, value);
  }

  /** Yellow pointer indicator on the current card. */
  setYellowPointer(on: boolean): void {
    this.setBoolAny('main', MainInputs.boolYellowPointer, on);
  }

  triggerShuffle(): void {
    this.fireTriggerAny('main', MainInputs.trigShuffle);
  }

  triggerCardReset(): void {
    this.fireTriggerAny('main', MainInputs.trigCardReset);
  }

  /** Card 6 needs both `CardFace6` set AND this trigger to actually reveal. */
  triggerPlayCard6(): void {
    this.fireTriggerAny('main', MainInputs.trigPlayCard6);
  }

  /**
   * Girl animation.
   * Mirrors `triggerGirlAnimation` / `setGirlAnimationType` in the Flutter
   * manager — the bloc only fires the trigger after `startBetEnd`.
   */
  triggerGirlAnim(type: 0 | 1 | 2 = 1): void {
    if (type > 0) this.setNumberAny('main', MainInputs.numGirlAnim, type);
    this.fireTriggerAny('main', MainInputs.trigGirlAnim);
  }

  /** Mirror the sound state to both artboards (`isSounOn` — sic). */
  setSound(on: boolean): void {
    this.setBoolAny('main', MainInputs.boolIsSound, on);
    this.setBoolAny('betting', MainInputs.boolIsSound, on);
  }

  /** Set a text run across all artboards that have it (no-throw). */
  setTextRunBroadcast(name: string, value: string): void {
    for (const t of this.targets.values()) {
      try {
        t.instance.setTextRunValue(name, value);
      } catch {
        /* not present on this artboard */
      }
    }
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level input plumbing
  // -----------------------------------------------------------------

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not number`);
      return false;
    }
    debugLog(`[Rive] number '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private setBoolAny(key: TargetKey, name: string, value: boolean): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not boolean`);
      return false;
    }
    debugLog(`[Rive] bool '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private fireTriggerAny(key: TargetKey, name: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not trigger`);
      return false;
    }
    debugLog(`[Rive] trigger '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private setNumberAtPath(
    key: TargetKey,
    inputName: string,
    value: number,
    path: string,
  ): void {
    const t = this.targets.get(key);
    if (!t) return;
    try {
      t.instance.setNumberStateAtPath(inputName, value, path);
    } catch (e) {
      debugLog(
        `[Rive] setNumberStateAtPath('${inputName}', ${value}, '${path}') failed on ${key}:`,
        e,
      );
    }
  }
}
